import { Component, ChangeDetectorRef } from '@angular/core';
import {Router, NavigationEnd, RouterOutlet, RouterModule, IsActiveMatchOptions, UrlTree} from '@angular/router';
import { CommonModule } from '@angular/common';
import { AuthService, User } from './services/auth.service';
import { WebSocketService } from './services/websocket.service';
import { OfflineService, PendingOperation } from './services/offline.service';
import { MenuItem } from 'primeng/api';
import { filter } from 'rxjs/operators';
import { Observable } from 'rxjs';

// PrimeNG Modules
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { MenuModule } from 'primeng/menu';
import { AvatarModule } from 'primeng/avatar';
import { ToastModule } from 'primeng/toast';
import { BadgeModule } from 'primeng/badge';
import { DialogModule } from 'primeng/dialog';
import {Menubar} from 'primeng/menubar';
import {Ripple} from 'primeng/ripple';
import {DatePickerModule} from 'primeng/datepicker';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    RouterModule,
    ButtonModule,
    TooltipModule,
    MenuModule,
    AvatarModule,
    ToastModule,
    BadgeModule,
    DialogModule,
    DatePickerModule,
    Menubar,
    Ripple
  ],
  templateUrl: './app.html',
})
export class App {
  appName = 'MarinaPark Admin';

  navItems: MenuItem[] = [
    {
      label: 'Dashboard',
      icon: 'fa-solid fa-chart-line',
      routerLink: ['/dashboard']
    },
    {
      label: 'Visits',
      icon: 'fa-solid fa-ship',
      items: [
        {
          label: 'Visits',
          routerLink: ['/visits'],
          icon: 'fa-solid fa-calendar-days'
        },
        {
          label: 'Guest visits',
          routerLink: ['/marina-visits'],
          icon: 'fa-solid fa-right-to-bracket'
        },
        {
          label: 'Resident stays',
          routerLink: ['/marina-visits/resident'],
          icon: 'fa-solid fa-house'
        },
        {
          label: 'Vessels',
          routerLink: ['/vessels'],
          icon: 'fa-solid fa-sailboat'
        },
        {
          label: 'Persons',
          routerLink: ['/persons'],
          icon: 'fa-solid fa-user'
        }
      ]
    },
    {
      label: 'Marinas',
      icon: 'fa-solid fa-anchor',
      items: [
        {
          label: 'Marinas',
          icon: 'pi pi-anchor',
          routerLink: '/marinas'
        },
        {
          label: 'Quays',
          icon: 'fa-solid fa-th-large',
          routerLink: '/quays'
        },
        {
          label: 'Berths',
          icon: 'pi pi-anchor',
          routerLink: '/berths'
        },
        {
          label: 'Buoys',
          icon: 'pi pi-circle',
          routerLink: '/buoys'
        },
        {
          label: 'Legal entities',
          icon: 'pi pi-building',
          routerLink: '/legal-entities'
        },
        {
          label: 'Services',
          icon: 'pi pi-cog',
          routerLink: '/marina-services'
        },
        {
          label: 'Users',
          icon: 'pi pi-users',
          routerLink: '/users'
        }
      ]
    },
    {
      label: 'Pricing',
      icon: 'fa-solid fa-tags',
      items: [
        {
          label: 'Seasons',
          icon: 'pi pi-calendar',
          routerLink: '/marina-prices/seasons'
        },
        {
          label: 'Price lists',
          icon: 'pi pi-list',
          routerLink: '/marina-prices/price-lists'
        },
        {
          label: 'Price rules',
          icon: 'pi pi-sliders-h',
          routerLink: '/marina-prices/price-rules'
        }
      ]
    },
    {
      label: 'Reporting',
      icon: 'fa-solid fa-file-export',
      routerLink: ['/reporting']
    },
  ];
  title = 'Marina Management System';
  sidebarVisible: boolean = false;
  currentRoute: string = '';
  isAuthenticated: boolean = false;
  currentUser: User | null = null;

  // Offline support
  isOnline$: Observable<boolean>;
  pendingOperations$: Observable<PendingOperation[]>;
  syncing$: Observable<boolean>;
  failedSync$: Observable<PendingOperation[]>;
  pendingChangesDialogVisible: boolean = false;

  // Track which submenus are expanded (by label)
  expandedMenus: Set<string> = new Set();

  // Base menu items for sidebar (will be filtered based on roles)
  private baseMenuItems: MenuItem[] = [
    {
      label: 'Dashboard',
      icon: 'pi pi-home',
      routerLink: '/dashboard'
    },
    {
      label: 'Guest Visits',
      icon: 'pi pi-calendar',
      routerLink: '/marina-visits'
    },
    {
      label: 'Resident stays',
      icon: 'pi pi-calendar',
      routerLink: '/marina-visits/resident'
    },
    {
      label: 'Vessels and Persons',
      icon: 'pi pi-ship',
      items: [
        {
          label: 'Vessels',
          icon: 'pi pi-ship',
          routerLink: '/vessels'
        },
        {
          label: 'Private Persons',
          icon: 'pi pi-user',
          routerLink: '/private-persons'
        }
      ]
    },
    {
      label: 'Contracts',
      icon: 'pi pi-file',
      routerLink: '/contracts'
    },
    {
      label: 'Marina Conf',
      icon: 'pi pi-sliders-v',
      items: [
        {
          label: 'Marinas',
          icon: 'pi pi-anchor',
          routerLink: '/marinas'
        },
        {
          label: 'Quays',
          icon: 'pi pi-th-large',
          routerLink: '/quays'
        },
        {
          label: 'Berths',
          icon: 'pi pi-anchor',
          routerLink: '/berths'
        },
        {
          label: 'Buoys',
          icon: 'pi pi-circle',
          routerLink: '/buoys'
        },
        {
          label: 'Legal Entities',
          icon: 'pi pi-building',
          routerLink: '/legal-entities'
        },
        {
          label: 'Services',
          icon: 'pi pi-cog',
          routerLink: '/marina-services'
        },
        {
          label: 'Users',
          icon: 'pi pi-users',
          routerLink: '/users'
        }
      ]
    },
    {
      label: 'Marina Prices',
      icon: 'pi pi-dollar',
      items: [
        {
          label: 'Seasons',
          icon: 'pi pi-calendar',
          routerLink: '/marina-prices/seasons'
        },
        {
          label: 'Price Lists',
          icon: 'pi pi-list',
          routerLink: '/marina-prices/price-lists'
        },
        {
          label: 'Price Rules',
          icon: 'pi pi-sliders-h',
          routerLink: '/marina-prices/price-rules'
        }
      ]
    },
    {
      label: 'Reporting',
      icon: 'pi pi-chart-bar',
      routerLink: '/reporting',
      badge: 'NEW',
      badgeClass: 'p-badge-success'
    }
  ];

  // Get filtered menu items based on user roles
  get menuItems(): MenuItem[] {
    if (!this.isAuthenticated) {
      return [];
    }

    const userRoles = this.authService.getUserRoles();
    const isOnlyUser = userRoles.length === 1 &&
      (userRoles[0].toUpperCase() === 'ROLE_USER' || userRoles[0].toUpperCase() === 'USER');

    if (isOnlyUser) {
      // ROLE_USER only - hide Marina Conf, Marina Prices, and Reporting
      return this.baseMenuItems.filter(item =>
        item.label !== 'Marina Conf' &&
        item.label !== 'Marina Prices' &&
        item.label !== 'Reporting'
      );
    }

    // All other roles (Admin, Manager, etc.) - show all menu items
    return this.baseMenuItems;
  }

  // User menu items
  userMenuItems: MenuItem[] = [
    {
      label: 'Profile',
      icon: 'pi pi-user',
      command: () => this.onProfileClick()
    },
    {
      separator: true
    },
    {
      label: 'Logout',
      icon: 'pi pi-sign-out',
      command: () => this.onLogout()
    }
  ];

  constructor(
    private router: Router,
    private authService: AuthService,
    private webSocketService: WebSocketService,
    private offlineService: OfflineService,
    private cdr: ChangeDetectorRef
  ) {
    // Initialize offline observables
    this.isOnline$ = this.offlineService.isOnline$;
    this.pendingOperations$ = this.offlineService.pendingOperations$;
    this.syncing$ = this.offlineService.syncing$;
    this.failedSync$ = this.offlineService.failedSync$;

    // Subscribe to authentication status
    this.authService.isAuthenticated$.subscribe(
      isAuth => {
        this.isAuthenticated = isAuth;

        // Connect to WebSocket when authenticated
        if (isAuth) {
          console.log('[App] User authenticated, connecting to WebSocket...');
          this.webSocketService.connect();
        } else {
          console.log('[App] User not authenticated, disconnecting WebSocket...');
          this.webSocketService.disconnect();
        }
      }
    );

    // Subscribe to user changes to update menu visibility
    this.authService.currentUser$.subscribe(user => {
      this.currentUser = user;
      if (user) {
        console.log('[App] User changed, updating menu visibility. Roles:', user.roles);
        // Trigger change detection to update menu
        this.cdr.detectChanges();
      }
    });

    this.currentRoute = this.router.url;

    // Track current route for active menu highlighting
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.currentRoute = event.url;
      // Auto-close all submenus when navigation occurs
      this.closeAllSubmenus();
    });
  }

  /** Top bar and menus only when signed in (login is full-page, no chrome). */
  get showAppChrome(): boolean {
    if (!this.isAuthenticated) {
      return false;
    }
    const path = (this.currentRoute || this.router.url || '').split(/[?#]/)[0];
    return path !== '/login';
  }

  // Navigation methods
  onMenuClick(): void {
    this.sidebarVisible = !this.sidebarVisible;
  }

  onProfileClick(): void {
    this.router.navigate(['/profile']);
  }

  onLogout(): void {
    console.log('[App] User logging out, disconnecting WebSocket...');
    this.webSocketService.disconnect();
    this.authService.logout();
    this.router.navigate(['/login']);
  }

  // Check if current route is active
  isActiveRoute(route: string | undefined): boolean {
    if (!route) return false;
    return this.currentRoute === route || this.currentRoute.startsWith(route + '/');
  }

  // Toggle submenu expansion
  toggleSubmenu(label: string | undefined): void {
    if (!label) return;
    if (this.expandedMenus.has(label)) {
      // If already expanded, close it
      this.expandedMenus.delete(label);
    } else {
      // Close all other submenus first (ensure only one is open at a time)
      this.closeAllSubmenus();
      // Then open the selected submenu
      this.expandedMenus.add(label);
    }
  }

  // Close all submenus
  closeAllSubmenus(): void {
    this.expandedMenus.clear();
  }

  // Check if submenu is expanded
  isSubmenuExpanded(label: string | undefined): boolean {
    if (!label) return false;
    return this.expandedMenus.has(label);
  }

  // Get current page title
  getCurrentPageTitle(): string {
    if (this.currentRoute.includes('/dashboard')) {
      return 'Dashboard';
    } else if (this.currentRoute.includes('/marina-visits/resident')) {
      return 'Resident stays';
    } else if (this.currentRoute.includes('/marinas/') && this.currentRoute.includes('/layout')) {
      return 'Marina Layout Editor';
    } else if (this.currentRoute.includes('/marinas/') && this.currentRoute.includes('/edit')) {
      return 'Edit Marina';
    } else if (this.currentRoute.includes('/marinas/new')) {
      return 'Add New Marina';
    } else if (this.currentRoute.includes('/marinas/') && !this.currentRoute.includes('/edit') && !this.currentRoute.includes('/layout')) {
      return 'Marina Details';
    } else if (this.currentRoute.includes('/marinas')) {
      return 'Marinas Management';
    } else if (this.currentRoute.includes('/legal-entities')) {
      return 'Legal Entities Management';
    } else if (this.currentRoute.includes('/vessels/') && this.currentRoute.includes('/edit')) {
      return 'Edit Vessel';
    } else if (this.currentRoute.includes('/vessels/new')) {
      return 'Add New Vessel';
    } else if (this.currentRoute.includes('/vessels/') && !this.currentRoute.includes('/edit')) {
      return 'Vessel Details';
    } else if (this.currentRoute.includes('/vessels')) {
      return 'Vessels';
    } else if (this.currentRoute.includes('/private-persons/') && this.currentRoute.includes('/edit')) {
      return 'Edit Private Person';
    } else if (this.currentRoute.includes('/private-persons/new')) {
      return 'Add New Private Person';
    } else if (this.currentRoute.includes('/private-persons/') && !this.currentRoute.includes('/edit')) {
      return 'Private Person Details';
    } else if (this.currentRoute.includes('/private-persons')) {
      return 'Private Persons';
    } else if (this.currentRoute.includes('/contracts/') && this.currentRoute.includes('/edit')) {
      return 'Edit Contract';
    } else if (this.currentRoute.includes('/contracts/new')) {
      return 'Add New Contract';
    } else if (this.currentRoute.includes('/contracts/') && !this.currentRoute.includes('/edit')) {
      return 'Contract Details';
    } else if (this.currentRoute.includes('/contracts')) {
      return 'Contracts';
    } else if (this.currentRoute.includes('/marina-visits/') && this.currentRoute.includes('/edit')) {
      return 'Edit Marina Visit';
    } else if (this.currentRoute.includes('/marina-visits/new')) {
      return 'Add New Marina Visit';
    } else if (this.currentRoute.includes('/marina-visits/') && !this.currentRoute.includes('/edit')) {
      return 'Marina Visit Details';
    } else if (this.currentRoute.includes('/marina-visits')) {
      return 'Guest Visits';
    } else if (this.currentRoute.includes('/marina-services/') && this.currentRoute.includes('/edit')) {
      return 'Edit Marina Service';
    } else if (this.currentRoute.includes('/marina-services/new')) {
      return 'Add New Service';
    } else if (this.currentRoute.includes('/marina-services/') && !this.currentRoute.includes('/edit')) {
      return 'Service Details';
    } else if (this.currentRoute.includes('/marina-services')) {
      return 'Marina Services';
    } else if (this.currentRoute.includes('/marina-prices/seasons/') && this.currentRoute.includes('/edit')) {
      return 'Edit Season';
    } else if (this.currentRoute.includes('/marina-prices/seasons/new')) {
      return 'Add New Season';
    } else if (this.currentRoute.includes('/marina-prices/seasons/') && !this.currentRoute.includes('/edit')) {
      return 'Season Details';
    } else if (this.currentRoute.includes('/marina-prices/seasons')) {
      return 'Marina Price Seasons';
    } else if (this.currentRoute.includes('/marina-prices/price-lists/') && this.currentRoute.includes('/edit')) {
      return 'Edit Price List';
    } else if (this.currentRoute.includes('/marina-prices/price-lists/new')) {
      return 'Add New Price List';
    } else if (this.currentRoute.includes('/marina-prices/price-lists/') && !this.currentRoute.includes('/edit')) {
      return 'Price List Details';
    } else if (this.currentRoute.includes('/marina-prices/price-lists')) {
      return 'Marina Price Lists';
    } else if (this.currentRoute.includes('/marina-prices/price-rules/') && this.currentRoute.includes('/edit')) {
      return 'Edit Price Rule';
    } else if (this.currentRoute.includes('/marina-prices/price-rules/new')) {
      return 'Add New Price Rule';
    } else if (this.currentRoute.includes('/marina-prices/price-rules/') && !this.currentRoute.includes('/edit')) {
      return 'Price Rule Details';
    } else if (this.currentRoute.includes('/marina-prices/price-rules')) {
      return 'Marina Price Rules';
    } else if (this.currentRoute.includes('/marina-prices')) {
      return 'Marina Prices Management';
    } else if (this.currentRoute.includes('/users/') && this.currentRoute.includes('/edit')) {
      return 'Edit User';
    } else if (this.currentRoute.includes('/users/new')) {
      return 'Create New User';
    } else if (this.currentRoute.includes('/users/') && !this.currentRoute.includes('/edit')) {
      return 'User Details';
    } else if (this.currentRoute.includes('/users')) {
      return 'User Management';
    } else if (this.currentRoute.includes('/profile')) {
      return 'My Profile';
    } else if (this.currentRoute.includes('/reporting')) {
      return 'Reporting Dashboard';
    } else if (this.currentRoute.includes('/login')) {
      return 'Login';
    } else if (this.currentRoute.includes('/dashboard')) {
      return 'Dashboard';
    } else {
      return 'Dashboard';
    }
  }

  // Get breadcrumb navigation
  getBreadcrumbs(): Array<{label: string, route?: string}> {
    if (this.currentRoute.includes('/dashboard')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Dashboard' }
      ];
    } else if (this.currentRoute.includes('/marina-visits/resident')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Resident stays' }
      ];
    } else if (this.currentRoute.includes('/marinas/') && this.currentRoute.includes('/layout')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Marinas', route: '/marinas' },
        { label: 'Layout Editor' }
      ];
    } else if (this.currentRoute.includes('/marinas/') && this.currentRoute.includes('/edit')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Marinas', route: '/marinas' },
        { label: 'Edit Marina' }
      ];
    } else if (this.currentRoute.includes('/marinas/new')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Marinas', route: '/marinas' },
        { label: 'Add New Marina' }
      ];
    } else if (this.currentRoute.includes('/marinas/') && !this.currentRoute.includes('/edit') && !this.currentRoute.includes('/layout')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Marinas', route: '/marinas' },
        { label: 'Marina Details' }
      ];
    } else if (this.currentRoute.includes('/marinas')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Marinas' }
      ];
    } else if (this.currentRoute.includes('/legal-entities')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Legal Entities' }
      ];
    } else if (this.currentRoute.includes('/vessels/') && this.currentRoute.includes('/edit')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Vessels', route: '/vessels' },
        { label: 'Edit Vessel' }
      ];
    } else if (this.currentRoute.includes('/vessels/new')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Vessels', route: '/vessels' },
        { label: 'Add New Vessel' }
      ];
    } else if (this.currentRoute.includes('/vessels/') && !this.currentRoute.includes('/edit')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Vessels', route: '/vessels' },
        { label: 'Vessel Details' }
      ];
    } else if (this.currentRoute.includes('/vessels')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Vessels' }
      ];
    } else if (this.currentRoute.includes('/private-persons/') && this.currentRoute.includes('/edit')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Private Persons', route: '/private-persons' },
        { label: 'Edit Private Person' }
      ];
    } else if (this.currentRoute.includes('/private-persons/new')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Private Persons', route: '/private-persons' },
        { label: 'Add New Private Person' }
      ];
    } else if (this.currentRoute.includes('/private-persons/') && !this.currentRoute.includes('/edit')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Private Persons', route: '/private-persons' },
        { label: 'Private Person Details' }
      ];
    } else if (this.currentRoute.includes('/private-persons')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Private Persons' }
      ];
    } else if (this.currentRoute.includes('/contracts/') && this.currentRoute.includes('/edit')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Contracts', route: '/contracts' },
        { label: 'Edit Contract' }
      ];
    } else if (this.currentRoute.includes('/contracts/new')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Contracts', route: '/contracts' },
        { label: 'Add New Contract' }
      ];
    } else if (this.currentRoute.includes('/contracts/') && !this.currentRoute.includes('/edit')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Contracts', route: '/contracts' },
        { label: 'Contract Details' }
      ];
    } else if (this.currentRoute.includes('/contracts')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Contracts' }
      ];
    } else if (this.currentRoute.includes('/marina-visits/') && this.currentRoute.includes('/edit')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Guest Visits', route: '/marina-visits' },
        { label: 'Edit Visit' }
      ];
    } else if (this.currentRoute.includes('/marina-visits/new')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Guest Visits', route: '/marina-visits' },
        { label: 'Add New Visit' }
      ];
    } else if (this.currentRoute.includes('/marina-visits/') && !this.currentRoute.includes('/edit')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Guest Visits', route: '/marina-visits' },
        { label: 'Visit Details' }
      ];
    } else if (this.currentRoute.includes('/marina-visits')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Guest Visits' }
      ];
    } else if (this.currentRoute.includes('/marina-services/') && this.currentRoute.includes('/edit')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Marina Services', route: '/marina-services' },
        { label: 'Edit Service' }
      ];
    } else if (this.currentRoute.includes('/marina-services/new')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Marina Services', route: '/marina-services' },
        { label: 'Add New Service' }
      ];
    } else if (this.currentRoute.includes('/marina-services/') && !this.currentRoute.includes('/edit')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Marina Services', route: '/marina-services' },
        { label: 'Service Details' }
      ];
    } else if (this.currentRoute.includes('/marina-services')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Marina Services' }
      ];
    } else if (this.currentRoute.includes('/marina-prices/seasons/') && this.currentRoute.includes('/edit')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Marina Prices', route: '/marina-prices/seasons' },
        { label: 'Seasons', route: '/marina-prices/seasons' },
        { label: 'Edit Season' }
      ];
    } else if (this.currentRoute.includes('/marina-prices/seasons/new')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Marina Prices', route: '/marina-prices/seasons' },
        { label: 'Seasons', route: '/marina-prices/seasons' },
        { label: 'Add New Season' }
      ];
    } else if (this.currentRoute.includes('/marina-prices/seasons/') && !this.currentRoute.includes('/edit')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Marina Prices', route: '/marina-prices/seasons' },
        { label: 'Seasons', route: '/marina-prices/seasons' },
        { label: 'Season Details' }
      ];
    } else if (this.currentRoute.includes('/marina-prices/seasons')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Marina Prices', route: '/marina-prices/seasons' },
        { label: 'Seasons' }
      ];
    } else if (this.currentRoute.includes('/marina-prices/price-lists/') && this.currentRoute.includes('/edit')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Marina Prices', route: '/marina-prices/price-lists' },
        { label: 'Price Lists', route: '/marina-prices/price-lists' },
        { label: 'Edit Price List' }
      ];
    } else if (this.currentRoute.includes('/marina-prices/price-lists/new')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Marina Prices', route: '/marina-prices/price-lists' },
        { label: 'Price Lists', route: '/marina-prices/price-lists' },
        { label: 'Add New Price List' }
      ];
    } else if (this.currentRoute.includes('/marina-prices/price-lists/') && !this.currentRoute.includes('/edit')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Marina Prices', route: '/marina-prices/price-lists' },
        { label: 'Price Lists', route: '/marina-prices/price-lists' },
        { label: 'Price List Details' }
      ];
    } else if (this.currentRoute.includes('/marina-prices/price-lists')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Marina Prices', route: '/marina-prices/price-lists' },
        { label: 'Price Lists' }
      ];
    } else if (this.currentRoute.includes('/marina-prices/price-rules/') && this.currentRoute.includes('/edit')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Marina Prices', route: '/marina-prices/price-rules' },
        { label: 'Price Rules', route: '/marina-prices/price-rules' },
        { label: 'Edit Price Rule' }
      ];
    } else if (this.currentRoute.includes('/marina-prices/price-rules/new')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Marina Prices', route: '/marina-prices/price-rules' },
        { label: 'Price Rules', route: '/marina-prices/price-rules' },
        { label: 'Add New Price Rule' }
      ];
    } else if (this.currentRoute.includes('/marina-prices/price-rules/') && !this.currentRoute.includes('/edit')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Marina Prices', route: '/marina-prices/price-rules' },
        { label: 'Price Rules', route: '/marina-prices/price-rules' },
        { label: 'Price Rule Details' }
      ];
    } else if (this.currentRoute.includes('/marina-prices/price-rules')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Marina Prices', route: '/marina-prices/price-rules' },
        { label: 'Price Rules' }
      ];
    } else if (this.currentRoute.includes('/marina-prices')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Marina Prices' }
      ];
    } else if (this.currentRoute.includes('/users/') && this.currentRoute.includes('/edit')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Users', route: '/users' },
        { label: 'Edit User' }
      ];
    } else if (this.currentRoute.includes('/users/new')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Users', route: '/users' },
        { label: 'Create New User' }
      ];
    } else if (this.currentRoute.includes('/users/') && !this.currentRoute.includes('/edit')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Users', route: '/users' },
        { label: 'User Details' }
      ];
    } else if (this.currentRoute.includes('/users')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Users' }
      ];
    } else if (this.currentRoute.includes('/profile')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'My Profile' }
      ];
    } else if (this.currentRoute.includes('/reporting')) {
      return [
        { label: 'Home', route: '/dashboard' },
        { label: 'Reporting' }
      ];
    } else {
      return [
        { label: 'Home', route: '/marina-visits' }
      ];
    }
  }

  // Get marina view route for back button
  getMarinaViewRoute(): string {
    if (this.currentRoute.includes('/marinas/') && this.currentRoute.includes('/layout')) {
      // Extract marina ID from current route
      const routeParts = this.currentRoute.split('/');
      const marinasIndex = routeParts.indexOf('marinas');
      if (marinasIndex !== -1 && routeParts[marinasIndex + 1]) {
        return `/marinas/${routeParts[marinasIndex + 1]}`;
      }
    }
    return '/marinas';
  }

  // Offline support methods
  showPendingChangesDialog(): void {
    this.pendingChangesDialogVisible = true;
  }

  hidePendingChangesDialog(): void {
    this.pendingChangesDialogVisible = false;
  }

  syncNow(): void {
    this.offlineService.syncPendingOperations();
  }

  openFailedOperation(op: PendingOperation): void {
    this.hidePendingChangesDialog();
    this.offlineService.openFailedOperation(op);
  }

  removePendingOperation(op: PendingOperation): void {
    this.offlineService.removePendingOperation(op.id);
  }

  getEntityTypeLabel(entityType: string): string {
    const labels: Record<string, string> = {
      'contract': 'Contract',
      'vessel': 'Vessel',
      'marina': 'Marina',
      'marina-visit': 'Marina Visit',
      'marina-service': 'Service',
      'legal-entity': 'Legal Entity',
      'price-season': 'Season',
      'price-list': 'Price List',
      'price-rule': 'Price Rule'
    };
    return labels[entityType] || entityType;
  }

  getOperationLabel(operation: string): string {
    const labels: Record<string, string> = {
      'create': 'Create',
      'update': 'Update',
      'delete': 'Delete'
    };
    return labels[operation] || operation;
  }

  formatTimestamp(timestamp: number): string {
    return new Date(timestamp).toLocaleString();
  }


  private exactMatch: IsActiveMatchOptions = {
    paths: 'exact',
    queryParams: 'ignored',
    fragment: 'ignored',
    matrixParams: 'ignored'
  };

  isExactActive(item: MenuItem): boolean {
    if (!item.routerLink) {
      return false;
    }

    return this.router.isActive(this.toUrlTree(item.routerLink), this.exactMatch);
  }

  isParentActive(item: MenuItem): boolean {
    return item.items?.some((child) => this.isExactActive(child) || this.isParentActive(child)) ?? false;
  }

  private toUrlTree(routerLink: string | unknown[]): UrlTree {
    return Array.isArray(routerLink)
      ? this.router.createUrlTree(routerLink)
      : this.router.parseUrl(routerLink);
  }

}
